import { Link } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { miniPlan, planTarget, svgPoints, type ItemWithPath, type Location } from '@ousse/shared'
import { api } from '../lib/api'
import { SeeIn3DForItem } from './SeeIn3D'

/**
 * Bloc « Où c'est ? » de la fiche objet (comme sur l'appli mobile) : étapes du chemin en liens, code d'étiquette
 * du rangement, mini-plan de la pièce avec le meuble en couleur (tiré de la géométrie de la vue 3D, rien si la
 * pièce n'est pas dessinée), puis « Voir en 3D » si l'offre le permet.
 */
export function WhereIsIt({ item, householdId }: { item: ItemWithPath; householdId: string }) {
  const { t } = useTranslation()
  // Même requête (et même cache) que l'écran d'ajout : tous les rangements du foyer, avec leur géométrie.
  const locations = useQuery({
    queryKey: ['locations', householdId],
    enabled: item.path.length > 0,
    queryFn: () => api.get<{ items: Location[] }>(`/households/${householdId}/locations`),
  })
  const all = locations.data?.items ?? []
  const last = item.path[item.path.length - 1]
  const here = last ? all.find((l) => l.id === last.id) : undefined
  const target = planTarget(item.path)
  const room = target ? all.find((l) => l.id === target.roomId) : undefined
  const plan = room
    ? miniPlan(
        room,
        all.filter((l) => l.parentId === room.id),
        target!.highlightId,
        { maxWidth: 480, maxHeight: 260 },
      )
    : null
  const highlighted = target?.highlightId ? all.find((l) => l.id === target.highlightId) : undefined
  const planLabel =
    room && highlighted
      ? t('inventory.planHighlight', { name: highlighted.name, room: room.name })
      : room
        ? t('inventory.planOf', { room: room.name })
        : ''

  return (
    <section className="card where" aria-labelledby="where-heading">
      <h2 id="where-heading" className="eyebrow">
        {t('inventory.whereIsIt')}
      </h2>
      {item.path.length === 0 ? (
        <p>
          <Link to="/storage?uncategorized=1">{t('inventory.uncategorized')}</Link>
        </p>
      ) : (
        <ol className="where-steps">
          {item.path.map((seg, i) => {
            const isLast = i === item.path.length - 1
            return (
              <li key={seg.id} className={isLast ? 'last' : undefined}>
                <span className="where-dot" aria-hidden />
                <Link to={`/storage?locationId=${seg.id}`} aria-current={isLast ? 'location' : undefined}>
                  {seg.name}
                </Link>
                {isLast && here?.publicCode && (
                  <span className="code-tag" aria-label={t('inventory.labelCode', { code: here.publicCode })}>
                    {here.publicCode}
                  </span>
                )}
              </li>
            )
          })}
        </ol>
      )}
      {plan && (
        <figure style={{ margin: '0 0 1rem' }}>
          <svg className="mini-plan" width={plan.width} height={plan.height} viewBox={`0 0 ${plan.width} ${plan.height}`} role="img" aria-label={planLabel}>
            <polygon className="room" points={svgPoints(plan.room)} />
            {plan.furniture.map((f) => (
              <polygon key={f.id} className={f.highlight ? 'furniture highlight' : 'furniture'} points={svgPoints(f.points)} />
            ))}
            {plan.pin && (
              <>
                <circle className="pin" cx={plan.pin.x} cy={plan.pin.y} r={9} />
                <circle className="pin-dot" cx={plan.pin.x} cy={plan.pin.y} r={3} />
              </>
            )}
          </svg>
          {highlighted && <figcaption className="muted">{planLabel}</figcaption>}
        </figure>
      )}
      {item.quantity > 1 && <p className="muted">{t('inventory.quantity', { count: item.quantity })}</p>}
      {item.description && <p>{item.description}</p>}
      {item.path.length > 0 && (
        <div className="row">
          <Link className="btn" to={`/storage?locationId=${last!.id}`}>
            {t('inventory.openStorage')}
          </Link>
          <SeeIn3DForItem item={item} />
        </div>
      )}
    </section>
  )
}
